import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useSeries } from '@/contexts/SeriesContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  BookOpen,
  Globe,
  Users,
  Palette,
  Eye,
  Plus,
  X,
  Sparkles,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { getBookTypeLabel } from '@/lib/utils';

// localStorage key for auto-save
const DRAFT_STORAGE_KEY = 'tellor_series_draft';

// One-time migration from old localStorage key
if (typeof window !== 'undefined') {
  const old = localStorage.getItem('storylab_series_draft');
  if (old && !localStorage.getItem(DRAFT_STORAGE_KEY)) {
    localStorage.setItem(DRAFT_STORAGE_KEY, old);
    localStorage.removeItem('storylab_series_draft');
  }
}

// Wizard steps
const STEPS = [
  { key: 'basics', label: 'Basics', icon: BookOpen },
  { key: 'world', label: 'World', icon: Globe },
  { key: 'characters', label: 'Characters', icon: Users },
  { key: 'style', label: 'Style', icon: Palette },
  { key: 'review', label: 'Review', icon: Eye }
];

const CreateSeriesPage = () => {
  const navigate = useNavigate();
  const { createSeries, loading, canCreateSeries, seriesLimit, fetchUserSeries } = useSeries();

  const [currentStep, setCurrentStep] = useState(0);
  const [premiumStatusLoaded, setPremiumStatusLoaded] = useState(false);

  // Fetch user series to get premium status on mount
  useEffect(() => {
    const loadPremiumStatus = async () => {
      await fetchUserSeries();
      setPremiumStatusLoaded(true);
    };
    loadPremiumStatus();
  }, [fetchUserSeries]);

  // Redirect non-premium users to the series list (which shows the preview)
  // Wait for premium status to be loaded before checking
  useEffect(() => {
    if (premiumStatusLoaded && seriesLimit && !seriesLimit.isPremium) {
      toast.error('Series is a premium feature', {
        description: 'Upgrade to Premium to create story series'
      });
      navigate('/series');
    }
  }, [premiumStatusLoaded, seriesLimit, navigate]);

  // Step 1: Basics
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ageRange, setAgeRange] = useState('5-8');

  // Step 2: World
  const [worldName, setWorldName] = useState('');
  const [worldDescription, setWorldDescription] = useState('');
  const [locations, setLocations] = useState(['']);
  const [worldRules, setWorldRules] = useState(['']);

  // Step 3: Characters
  const [characters, setCharacters] = useState([
    { name: '', role: 'protagonist', description: '', traits: [''] }
  ]);

  // Step 4: Style
  const [artStyle, setArtStyle] = useState('storybook');
  const [colorPalette, setColorPalette] = useState('warm_pastels');
  const [narrativeTone, setNarrativeTone] = useState('warm');

  // Auto-save state
  const [lastSaved, setLastSaved] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        // Check if draft is less than 7 days old
        if (draft.timestamp && Date.now() - draft.timestamp < 7 * 24 * 60 * 60 * 1000) {
          // Restore state from draft
          if (draft.title) setTitle(draft.title);
          if (draft.description) setDescription(draft.description);
          if (draft.ageRange) setAgeRange(draft.ageRange);
          if (draft.worldName) setWorldName(draft.worldName);
          if (draft.worldDescription) setWorldDescription(draft.worldDescription);
          if (draft.locations) setLocations(draft.locations);
          if (draft.worldRules) setWorldRules(draft.worldRules);
          if (draft.characters) setCharacters(draft.characters);
          if (draft.artStyle) setArtStyle(draft.artStyle);
          if (draft.colorPalette) setColorPalette(draft.colorPalette);
          if (draft.narrativeTone) setNarrativeTone(draft.narrativeTone);
          if (typeof draft.currentStep === 'number') setCurrentStep(draft.currentStep);
          setHasDraft(true);
          setLastSaved(new Date(draft.timestamp));
          toast.success('Draft restored', { description: 'Your previous progress has been loaded' });
        } else {
          // Draft is too old, clear it
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      }
    } catch (e) {
      console.error('Failed to load draft:', e);
    }
    // Mark initial load as complete
    setTimeout(() => { isInitialLoad.current = false; }, 100);
  }, []);

  // Auto-save function
  const saveDraft = useCallback(() => {
    // Don't save if this is the initial load or form is empty
    if (isInitialLoad.current || !title.trim()) return;

    const draft = {
      title,
      description,
      ageRange,
      worldName,
      worldDescription,
      locations,
      worldRules,
      characters,
      artStyle,
      colorPalette,
      narrativeTone,
      currentStep,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      setLastSaved(new Date());
      setHasDraft(true);
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
  }, [title, description, ageRange, worldName, worldDescription, locations, worldRules, characters, artStyle, colorPalette, narrativeTone, currentStep]);

  // Auto-save on form changes (debounced)
  useEffect(() => {
    if (isInitialLoad.current) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout to save after 2 seconds of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      saveDraft();
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [saveDraft]);

  // Clear draft on successful creation
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      setHasDraft(false);
      setLastSaved(null);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  }, []);

  // Validation per step
  const validateStep = (step) => {
    switch (step) {
      case 0: // Basics
        return title.trim().length >= 2;
      case 1: // World (optional)
        return true;
      case 2: // Characters (at least one with name)
        return characters.some(c => c.name.trim().length >= 2);
      case 3: // Style
        return artStyle && colorPalette && narrativeTone;
      case 4: // Review
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in the required fields');
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Character management
  const addCharacter = () => {
    if (characters.length < 6) {
      setCharacters([...characters, { name: '', role: 'supporting', description: '', traits: [''] }]);
    }
  };

  const removeCharacter = (index) => {
    if (characters.length > 1) {
      setCharacters(characters.filter((_, i) => i !== index));
    }
  };

  const updateCharacter = (index, field, value) => {
    const updated = [...characters];
    updated[index][field] = value;
    setCharacters(updated);
  };

  const addCharacterTrait = (charIndex) => {
    const updated = [...characters];
    if (updated[charIndex].traits.length < 5) {
      updated[charIndex].traits.push('');
      setCharacters(updated);
    }
  };

  const updateCharacterTrait = (charIndex, traitIndex, value) => {
    const updated = [...characters];
    updated[charIndex].traits[traitIndex] = value;
    setCharacters(updated);
  };

  const removeCharacterTrait = (charIndex, traitIndex) => {
    const updated = [...characters];
    if (updated[charIndex].traits.length > 1) {
      updated[charIndex].traits = updated[charIndex].traits.filter((_, i) => i !== traitIndex);
      setCharacters(updated);
    }
  };

  // Location management
  const addLocation = () => {
    if (locations.length < 5) {
      setLocations([...locations, '']);
    }
  };

  const updateLocation = (index, value) => {
    const updated = [...locations];
    updated[index] = value;
    setLocations(updated);
  };

  const removeLocation = (index) => {
    if (locations.length > 1) {
      setLocations(locations.filter((_, i) => i !== index));
    }
  };

  // World rules management
  const addWorldRule = () => {
    if (worldRules.length < 5) {
      setWorldRules([...worldRules, '']);
    }
  };

  const updateWorldRule = (index, value) => {
    const updated = [...worldRules];
    updated[index] = value;
    setWorldRules(updated);
  };

  const removeWorldRule = (index) => {
    if (worldRules.length > 1) {
      setWorldRules(worldRules.filter((_, i) => i !== index));
    }
  };

  // Submit series
  const handleSubmit = async () => {
    if (!canCreateSeries()) {
      toast.error('Series limit reached. Upgrade to Premium for unlimited series.');
      navigate('/pricing');
      return;
    }

    try {
      const seriesData = {
        title: title.trim(),
        description: description.trim() || '',
        age_range: ageRange,
        art_style: artStyle,
        color_palette: colorPalette,
        narrative_tone: narrativeTone,
        world: worldName.trim() ? {
          name: worldName.trim(),
          description: worldDescription.trim() || '',
          locations: locations.filter(l => l.trim()),
          rules: worldRules.filter(r => r.trim())
        } : null,
        characters: characters
          .filter(c => c.name.trim())
          .map(c => ({
            name: c.name.trim(),
            role: c.role,
            description: c.description.trim() || '',
            traits: c.traits.filter(t => t.trim())
          }))
      };

      const newSeries = await createSeries(seriesData);
      clearDraft(); // Clear saved draft on success
      toast.success('Series created successfully!');
      navigate(`/series/${newSeries.id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create series');
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Series Title <span className="text-red-400">*</span>
              </Label>
              <Input
                placeholder="e.g., The Adventures of Luna & Max"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
                className="border rounded-xl h-12 text-base"
                style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
              />
              <p className="text-xs mt-1.5" style={{ color: 'var(--st-text-muted)' }}>{title.length}/100</p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Description
              </Label>
              <Textarea
                placeholder="What is this series about? What makes it special?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                className="border rounded-xl min-h-[100px] text-base resize-none"
                style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
              />
              <p className="text-xs mt-1.5" style={{ color: 'var(--st-text-muted)' }}>{description.length}/500</p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Book Format <span className="text-red-400">*</span>
              </Label>
              <Select value={ageRange} onValueChange={setAgeRange}>
                <SelectTrigger className="border rounded-xl h-12" style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3-5">Picture Book</SelectItem>
                  <SelectItem value="5-8">Easy Reader</SelectItem>
                  <SelectItem value="8-12">Chapter Book</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-5">
            <div className="p-4 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
              <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                World building is optional. Define a consistent setting for all books in your series.
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                World Name
              </Label>
              <Input
                placeholder="e.g., The Enchanted Forest"
                value={worldName}
                onChange={(e) => setWorldName(e.target.value)}
                maxLength={100}
                className="border rounded-xl h-12 text-base"
                style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                World Description
              </Label>
              <Textarea
                placeholder="Describe the world where your stories take place..."
                value={worldDescription}
                onChange={(e) => setWorldDescription(e.target.value)}
                maxLength={500}
                className="border rounded-xl min-h-[80px] text-base resize-none"
                style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Key Locations
              </Label>
              <div className="space-y-2">
                {locations.map((location, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`Location ${index + 1}`}
                      value={location}
                      onChange={(e) => updateLocation(index, e.target.value)}
                      className="border rounded-xl h-10 text-sm flex-1"
                      style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                    />
                    {locations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLocation(index)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {locations.length < 5 && (
                  <button
                    type="button"
                    onClick={addLocation}
                    className="text-sm px-3 py-1.5 rounded-lg font-medium"
                    style={{ color: '#6366F1' }}
                  >
                    + Add Location
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                World Rules
              </Label>
              <p className="text-xs mb-2" style={{ color: 'var(--st-text-muted)' }}>
                Special rules or magic systems in your world
              </p>
              <div className="space-y-2">
                {worldRules.map((rule, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`Rule ${index + 1} (e.g., Animals can talk)`}
                      value={rule}
                      onChange={(e) => updateWorldRule(index, e.target.value)}
                      className="border rounded-xl h-10 text-sm flex-1"
                      style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                    />
                    {worldRules.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWorldRule(index)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {worldRules.length < 5 && (
                  <button
                    type="button"
                    onClick={addWorldRule}
                    className="text-sm px-3 py-1.5 rounded-lg font-medium"
                    style={{ color: '#6366F1' }}
                  >
                    + Add Rule
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5">
            <div className="p-4 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
              <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Add at least one character. Characters will appear consistently across all books in your series.
              </p>
            </div>

            {characters.map((char, charIndex) => (
              <div
                key={charIndex}
                className="p-4 rounded-xl space-y-3"
                style={{ background: 'var(--st-bg-muted)', border: '1px solid var(--st-border-light)' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold" style={{ color: 'var(--st-text-heading)' }}>
                    Character {charIndex + 1}
                  </span>
                  {characters.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCharacter(charIndex)}
                      className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Name *</Label>
                    <Input
                      placeholder="Character name"
                      value={char.name}
                      onChange={(e) => updateCharacter(charIndex, 'name', e.target.value)}
                      className="border rounded-lg h-9 text-sm"
                      style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-surface)' }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Role</Label>
                    <Select
                      value={char.role}
                      onValueChange={(value) => updateCharacter(charIndex, 'role', value)}
                    >
                      <SelectTrigger className="border rounded-lg h-9 text-sm" style={{ borderColor: 'var(--st-border-light)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="protagonist">Protagonist</SelectItem>
                        <SelectItem value="supporting">Supporting</SelectItem>
                        <SelectItem value="antagonist">Antagonist</SelectItem>
                        <SelectItem value="mentor">Mentor</SelectItem>
                        <SelectItem value="sidekick">Sidekick</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Description</Label>
                  <Textarea
                    placeholder="Physical appearance, personality, backstory..."
                    value={char.description}
                    onChange={(e) => updateCharacter(charIndex, 'description', e.target.value)}
                    className="border rounded-lg min-h-[60px] text-sm resize-none"
                    style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-surface)' }}
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Traits</Label>
                  <div className="flex flex-wrap gap-2">
                    {char.traits.map((trait, traitIndex) => (
                      <div key={traitIndex} className="flex items-center gap-1">
                        <Input
                          placeholder="e.g., brave"
                          value={trait}
                          onChange={(e) => updateCharacterTrait(charIndex, traitIndex, e.target.value)}
                          className="border rounded-lg h-8 text-xs w-24"
                          style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-surface)' }}
                        />
                        {char.traits.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCharacterTrait(charIndex, traitIndex)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    {char.traits.length < 5 && (
                      <button
                        type="button"
                        onClick={() => addCharacterTrait(charIndex)}
                        className="text-xs px-2 py-1 rounded-lg"
                        style={{ color: '#6366F1' }}
                      >
                        + Trait
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {characters.length < 6 && (
              <button
                type="button"
                onClick={addCharacter}
                className="w-full py-3 rounded-xl border-2 border-dashed font-medium text-sm flex items-center justify-center gap-2"
                style={{ borderColor: '#E0E7FF', color: '#6366F1' }}
              >
                <Plus className="w-4 h-4" />
                Add Character
              </button>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Art Style
              </Label>
              <Select value={artStyle} onValueChange={setArtStyle}>
                <SelectTrigger className="border rounded-xl h-12" style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="watercolor">Watercolor</SelectItem>
                  <SelectItem value="cartoon">Cartoon</SelectItem>
                  <SelectItem value="storybook">Storybook Classic</SelectItem>
                  <SelectItem value="classic">Classic Illustration</SelectItem>
                  <SelectItem value="anime">Anime</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Color Palette
              </Label>
              <Select value={colorPalette} onValueChange={setColorPalette}>
                <SelectTrigger className="border rounded-xl h-12" style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warm_pastels">Warm Pastels</SelectItem>
                  <SelectItem value="bright_colors">Bright & Bold</SelectItem>
                  <SelectItem value="earthy_tones">Earthy Tones</SelectItem>
                  <SelectItem value="cool_blues">Cool Blues</SelectItem>
                  <SelectItem value="vintage">Vintage</SelectItem>
                  <SelectItem value="monochrome">Monochrome</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>
                Narrative Tone
              </Label>
              <Select value={narrativeTone} onValueChange={setNarrativeTone}>
                <SelectTrigger className="border rounded-xl h-12" style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warm">Warm & Cozy</SelectItem>
                  <SelectItem value="funny">Funny & Silly</SelectItem>
                  <SelectItem value="adventurous">Adventurous</SelectItem>
                  <SelectItem value="magical">Magical</SelectItem>
                  <SelectItem value="inspiring">Inspiring</SelectItem>
                  <SelectItem value="mysterious">Mysterious</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-5">
            <div className="text-center mb-4">
              <Sparkles className="w-10 h-10 mx-auto mb-2" style={{ color: '#6366F1' }} />
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Ready to Create Your Series!
              </h3>
            </div>

            <div className="space-y-3">
              {/* Basics */}
              <div className="p-4 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
                <h4 className="text-sm font-bold mb-2" style={{ color: 'var(--st-text-heading)' }}>Basic Info</h4>
                <p className="text-sm" style={{ color: 'var(--st-text-muted)' }}>
                  <strong>Title:</strong> {title}
                </p>
                {description && (
                  <p className="text-sm mt-1" style={{ color: 'var(--st-text-muted)' }}>
                    <strong>Description:</strong> {description}
                  </p>
                )}
                <p className="text-sm mt-1" style={{ color: 'var(--st-text-muted)' }}>
                  <strong>Book Format:</strong> {getBookTypeLabel(ageRange)}
                </p>
              </div>

              {/* World */}
              {worldName && (
                <div className="p-4 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
                  <h4 className="text-sm font-bold mb-2" style={{ color: 'var(--st-text-heading)' }}>World</h4>
                  <p className="text-sm" style={{ color: 'var(--st-text-muted)' }}>
                    <strong>Name:</strong> {worldName}
                  </p>
                  {locations.filter(l => l.trim()).length > 0 && (
                    <p className="text-sm mt-1" style={{ color: 'var(--st-text-muted)' }}>
                      <strong>Locations:</strong> {locations.filter(l => l.trim()).join(', ')}
                    </p>
                  )}
                </div>
              )}

              {/* Characters */}
              <div className="p-4 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
                <h4 className="text-sm font-bold mb-2" style={{ color: 'var(--st-text-heading)' }}>
                  Characters ({characters.filter(c => c.name.trim()).length})
                </h4>
                <div className="space-y-1">
                  {characters.filter(c => c.name.trim()).map((char, index) => (
                    <p key={index} className="text-sm" style={{ color: 'var(--st-text-muted)' }}>
                      <strong>{char.name}</strong> - {char.role}
                    </p>
                  ))}
                </div>
              </div>

              {/* Style */}
              <div className="p-4 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
                <h4 className="text-sm font-bold mb-2" style={{ color: 'var(--st-text-heading)' }}>Style</h4>
                <p className="text-sm" style={{ color: 'var(--st-text-muted)' }}>
                  <strong>Art:</strong> {artStyle} | <strong>Colors:</strong> {colorPalette} | <strong>Tone:</strong> {narrativeTone}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-xl mx-auto px-4 py-4 md:py-6 md:px-8">

          {/* Header */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/series')}
                className="p-2 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Create New Series
                  <span
                    className="px-2 py-0.5 rounded text-xs font-bold"
                    style={{ background: '#E0E7FF', color: '#4F46E5', fontFamily: 'Quicksand, sans-serif' }}
                  >
                    Beta
                  </span>
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Build your interconnected storybook universe
                  </p>
                  {hasDraft && lastSaved && (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full dark:bg-green-900/40" style={{ background: '#DCFCE7', color: '#15803D' }}>
                      <Save className="w-3 h-3" />
                      Saved
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Centered Form Container */}
          <div className="max-w-5xl mx-auto">
            {/* Progress Steps */}
            <div className="mb-6 overflow-x-auto">
              <div className="flex items-center justify-center gap-2 min-w-max pb-2">
                {STEPS.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === currentStep;
                  const isCompleted = index < currentStep;

                  return (
                    <div key={step.key} className="flex items-center">
                      <button
                        onClick={() => {
                          if (index < currentStep || validateStep(currentStep)) {
                            setCurrentStep(index);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                          isActive ? 'shadow-sm' : ''
                        } ${isCompleted ? 'dark:bg-green-900/40' : ''}`}
                        style={{
                          background: isActive
                            ? 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)'
                            : isCompleted
                              ? '#DCFCE7'
                              : 'var(--st-bg-muted)',
                          color: isActive ? 'white' : isCompleted ? '#15803D' : 'var(--st-text-muted)'
                        }}
                      >
                        {isCompleted ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                        <span className="text-xs font-semibold hidden sm:inline">{step.label}</span>
                      </button>
                      {index < STEPS.length - 1 && (
                        <div
                          className="w-6 h-0.5 mx-1"
                          style={{ background: isCompleted ? '#15803D' : 'var(--st-border-light)' }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form Card */}
            <div className="rounded-2xl p-5 md:p-8 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              <h2 className="text-lg font-bold mb-5" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                {STEPS[currentStep].label}
              </h2>

              {renderStepContent()}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-5 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50"
                  style={{
                    background: 'var(--st-bg-muted)',
                    color: 'var(--st-text-heading)',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                {currentStep < STEPS.length - 1 ? (
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm"
                    style={{
                      background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                      color: 'white',
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50"
                    style={{
                      background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                      color: 'white',
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Create Series
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CreateSeriesPage;
